import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import {
  Plus, FileSpreadsheet, Pencil, Trash2, Search, X,
  Filter, Loader2, ArrowUpDown, ChevronRight, Check,
  ChevronDown, RefreshCw, AlertCircle, Clock,
} from 'lucide-react';
import { PageSizeNav } from '@/components/Service/PageSizeNav';
import { DateRangePresets, computePresetRange } from '@/components/Service/DateRangePresets';
import { BulkEditModal } from '@/components/Service/BulkEditModal';
import { SLADot, SLABadge as SLABadgeInline } from '@/components/Service/SLABadge';
import { MultiSelectFilter } from '@/components/Service/MultiSelectFilter';
import { useServiceMastersForFilters } from '@/hooks/useServiceMasters';
import { StatusPill } from '@/components/Service/StatusPill';
import { jobMetrics } from '@/components/Service/jobMetrics';
import { LastNotifyChip } from '@/components/Service/LastNotifyChip';
import { usePermissions, RESOURCES } from '@/contexts/PermissionsContext';
import { useServiceConfig } from '@/hooks/useServiceConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getCurrentFY = () => {
  const t = new Date();
  return t.getMonth() >= 3 ? `${t.getFullYear()}-${t.getFullYear() + 1}` : `${t.getFullYear() - 1}-${t.getFullYear()}`;
};
const fyOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => `${y - i}-${y - i + 1}`);
};

// Parse "DD-MM-YYYY" or "YYYY-MM-DD" to Date
const parseDate = (s) => {
  if (!s) return null;
  const parts = String(s).split(/[-/]/);
  if (parts.length !== 3) return null;
  let d, m, y;
  if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; }
  else { d = parts[0]; m = parts[1]; y = parts[2]; if (y.length === 2) y = '20' + y; }
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return isNaN(dt.getTime()) ? null : dt;
};

// iter-120: Session-storage key for filter retention across edit/view round-trips.
// Persists filter state for the All Service Jobs page within the current browser
// session only — resets on logout / new tab. Isolated per-page to avoid cross-
// report contamination.
const FILTERS_STORAGE_KEY = 'service:jobs:filters';
const FILTERS_SCHEMA_VERSION = 1;

const loadSavedFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.__v !== FILTERS_SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveFilters = (snapshot) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ __v: FILTERS_SCHEMA_VERSION, ...snapshot }));
  } catch { /* noop — storage might be disabled */ }
};

export default function ServiceJobsList({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSla = queryParams.get('sla') || 'all';
  const { canDelete } = usePermissions();
  const canDeleteService = canDelete(RESOURCES.SERVICE);
  const initialStatusGroup = queryParams.get('status_group') || '';
  const initialDateToday = queryParams.get('date_today') === '1';

  // iter-120: Filter retention — load any saved snapshot once on mount.
  // URL params (Dashboard tile navigation, SLA digest links) take priority;
  // saved snapshot is the fallback; module defaults win when both are absent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const savedSnapshot = React.useMemo(() => loadSavedFilters(), []);
  const hasUrlFilters = !!(
    queryParams.get('date_from') || queryParams.get('date_to') || queryParams.get('date_today') ||
    queryParams.get('sla') || queryParams.get('status_group') ||
    queryParams.get('doc_type') || queryParams.get('technician') || queryParams.get('supervisor') ||
    queryParams.get('service_head') || queryParams.get('job_type')
  );
  const useSaved = !hasUrlFilters && !!savedSnapshot;

  // Optional category pre-selection from Dashboard breakdown tile click
  const initialFilters = React.useMemo(() => {
    const f = { doc_type: [], technician: [], supervisor: [], service_head: [], job_type: [] };
    ['doc_type', 'technician', 'supervisor', 'service_head', 'job_type'].forEach((k) => {
      const v = queryParams.get(k);
      if (v) f[k] = v.split(',').map((s) => s.trim()).filter(Boolean);
    });
    if (useSaved && savedSnapshot.filters) {
      return { ...f, ...savedSnapshot.filters };
    }
    return f;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Today's date in DD-MM-YYYY for date defaulting
  const todayStr = React.useMemo(() => {
    const t = new Date();
    return `${String(t.getDate()).padStart(2, '0')}-${String(t.getMonth() + 1).padStart(2, '0')}-${t.getFullYear()}`;
  }, []);

  const urlDateFrom = queryParams.get('date_from') || '';
  const urlDateTo = queryParams.get('date_to') || '';

  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Top-level filters
  const [selectedFY, setSelectedFY] = React.useState(useSaved && savedSnapshot.selectedFY ? savedSnapshot.selectedFY : getCurrentFY());
  const [search, setSearch] = React.useState(useSaved && typeof savedSnapshot.search === 'string' ? savedSnapshot.search : '');
  // Multi-select filters — arrays
  const [filters, setFilters] = React.useState(initialFilters);
  // Default-exclude toggles (off = exclude)
  const [showPdi, setShowPdi] = React.useState(useSaved ? !!savedSnapshot.showPdi : false);
  const [showDelivered, setShowDelivered] = React.useState(useSaved ? !!savedSnapshot.showDelivered : false);
  // iter-114: segmented "All / Regular / Quick" job-type chip
  const [jobTypeView, setJobTypeView] = React.useState(useSaved && savedSnapshot.jobTypeView ? savedSnapshot.jobTypeView : 'all'); // 'all' | 'regular' | 'quick'

  // iter-120: default date range is Indian FY YTD (Apr 1 → today) for the
  // vehicle_in_date filter. URL params still override (e.g., Dashboard tile
  // click with explicit date_from/date_to or date_today=1 for Today's Jobs).
  // Saved session snapshot (if any) is the fallback before YTD defaults.
  const [activePreset, setActivePreset] = React.useState(
    urlDateFrom || urlDateTo ? '' :
    (initialDateToday ? 'today' :
    (useSaved && typeof savedSnapshot.activePreset === 'string' ? savedSnapshot.activePreset : 'ytd'))
  );
  const [dateFrom, setDateFrom] = React.useState(() =>
    urlDateFrom || (initialDateToday ? todayStr :
    (useSaved && savedSnapshot.dateFrom ? savedSnapshot.dateFrom : computePresetRange('ytd')[0]))
  );
  const [dateTo, setDateTo] = React.useState(() =>
    urlDateTo || (initialDateToday ? todayStr :
    (useSaved && savedSnapshot.dateTo ? savedSnapshot.dateTo : computePresetRange('ytd')[1]))
  );

  // Status group from dashboard click (queue|inprogress|completed)
  const [statusGroup, setStatusGroup] = React.useState(initialStatusGroup || (useSaved && savedSnapshot.statusGroup) || '');

  // Service masters — filter-visible subset only (hidden masters still render
  // in existing job rows because table cells read j.doc_type etc. directly).
  const masters = useServiceMastersForFilters();
  // Field visibility configuration (admin-controlled — defaults to fully visible)
  const { visibility } = useServiceConfig();
  const jcCols = visibility.job_cards.columns;
  const jcFilters = visibility.job_cards.filters;

  // Sorting & paging
  const [sortKey, setSortKey] = React.useState(useSaved && savedSnapshot.sortKey ? savedSnapshot.sortKey : 'job_no_int');
  const [sortDir, setSortDir] = React.useState(useSaved && savedSnapshot.sortDir ? savedSnapshot.sortDir : 'desc');
  const [pageSize, setPageSize] = React.useState(useSaved && savedSnapshot.pageSize ? savedSnapshot.pageSize : 10);
  const [page, setPage] = React.useState(1);

  // SLA quick filter
  const [slaFilter, setSlaFilter] = React.useState(
    queryParams.get('sla') ? initialSla :
    (useSaved && savedSnapshot.slaFilter ? savedSnapshot.slaFilter : initialSla)
  );

  // Selection + expand
  const [selected, setSelected] = React.useState(new Set());
  const [expanded, setExpanded] = React.useState(new Set());

  // Bulk edit modal
  const [bulkEditOpen, setBulkEditOpen] = React.useState(false);

  // Top horizontal scroll mirror
  const topScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const [tableWidth, setTableWidth] = React.useState(0);

  const fetchJobs = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service/jobs?financial_year=${selectedFY}&limit=10000`);
      setJobs(data || []);
      setSelected(new Set());
      setExpanded(new Set());
    } catch {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  React.useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // iter-120: Persist the current filter snapshot to sessionStorage on every
  // change so the user returns to the exact same view after editing/viewing
  // a job. Session-only — resets on logout / new tab / window close.
  React.useEffect(() => {
    saveFilters({
      search,
      filters,
      dateFrom,
      dateTo,
      activePreset,
      showPdi,
      showDelivered,
      jobTypeView,
      statusGroup,
      slaFilter,
      sortKey,
      sortDir,
      pageSize,
      selectedFY,
    });
  }, [search, filters, dateFrom, dateTo, activePreset, showPdi, showDelivered,
      jobTypeView, statusGroup, slaFilter, sortKey, sortDir, pageSize, selectedFY]);

  // Sync top scrollbar width to table width
  React.useEffect(() => {
    const t = tableScrollRef.current;
    if (!t) return;
    const ro = new ResizeObserver(() => {
      const inner = t.querySelector('table');
      if (inner) setTableWidth(inner.scrollWidth);
    });
    ro.observe(t);
    return () => ro.disconnect();
  }, [jobs.length]);

  const onTopScroll = (e) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  const onTableScroll = (e) => {
    if (topScrollRef.current) topScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  const uniqueValues = React.useMemo(() => {
    const t = new Set(), s = new Set(), sh = new Set(), jt = new Set(), st = new Set();
    jobs.forEach((j) => {
      if (j.technician) t.add(j.technician);
      if (j.supervisor) s.add(j.supervisor);
      if (j.service_head) sh.add(j.service_head);
      if (j.job_type) jt.add(j.job_type);
      if (j.doc_type) st.add(j.doc_type);
    });
    return {
      technician: [...t].sort(),
      supervisor: [...s].sort(),
      service_head: [...sh].sort(),
      job_type: [...jt].sort(),
      doc_type: [...st].sort(),
    };
  }, [jobs]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const df = parseDate(dateFrom);
    const dt = parseDate(dateTo);

    return jobs.filter((j) => {
      if (q) {
        const hay = `${j.job_no || ''} ${j.party_name || ''} ${j.reg_no || ''} ${j.chassis_no || ''} ${j.model || ''} ${j.mobile_no_1 || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Multi-select filters — empty array means "all"
      if (filters.doc_type.length && !filters.doc_type.includes(j.doc_type)) return false;
      if (filters.technician.length && !filters.technician.includes(j.technician)) return false;
      if (filters.supervisor.length && !filters.supervisor.includes(j.supervisor)) return false;
      if (filters.service_head.length && !filters.service_head.includes(j.service_head)) return false;
      if (filters.job_type.length && !filters.job_type.includes(j.job_type)) return false;

      // iter-114 — Quick / Regular / All segmented filter
      if (jobTypeView === 'quick' && !j.is_quick) return false;
      if (jobTypeView === 'regular' && j.is_quick) return false;

      // Default exclusions — toggle to include
      if (!showPdi) {
        const sh = String(j.service_head || '').toLowerCase();
        const jt = String(j.job_type || '').toLowerCase();
        if (sh.includes('pdi') || jt.includes('pdi')) return false;
      }
      if (!showDelivered) {
        const ds = String(j.doc_type || '').toLowerCase();
        if (ds.includes('deliver')) return false;
      }

      // Status group from dashboard click
      if (statusGroup) {
        const ds = String(j.doc_type || '').toLowerCase();
        if (statusGroup === 'queue' && !/queue|pending/.test(ds)) return false;
        if (statusGroup === 'inprogress' && !/progress|repair/.test(ds)) return false;
        if (statusGroup === 'completed' && !/complete|deliver|invoice/.test(ds)) return false;
      }

      // Date range filter on Vehicle In Date
      if (df || dt) {
        const jd = parseDate(j.vehicle_in_date);
        if (!jd) return false;
        if (df && jd < df) return false;
        if (dt && jd > dt) return false;
      }

      // SLA quick filter
      if (slaFilter === 'overdue' && j.sla_status !== 'overdue') return false;
      if (slaFilter === 'at_risk' && j.sla_status !== 'at_risk') return false;
      if (slaFilter === 'active' && (j.sla_status === 'completed' || j.sla_status === 'unknown')) return false;
      return true;
    });
  }, [jobs, search, filters, dateFrom, dateTo, slaFilter, showPdi, showDelivered, statusGroup, jobTypeView]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);
  React.useEffect(() => { setPage(1); }, [search, filters, selectedFY, sortKey, sortDir, dateFrom, dateTo, pageSize]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const togglePick = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    const pageIds = paged.map((j) => j.id);
    const allSelected = pageIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete Job #${job.job_no}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/service/jobs/${job.id}`);
      toast.success('Job deleted');
      fetchJobs();
    } catch {
      toast.error('Delete failed');
    }
  };
  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected job(s)? This cannot be undone.`)) return;
    try {
      const { data } = await axios.post(`${API}/service/jobs/bulk-delete`, { ids: [...selected] });
      toast.success(`${data.deleted} job(s) deleted`);
      fetchJobs();
    } catch {
      toast.error('Bulk delete failed');
    }
  };
  const handleBulkUpdate = async (updates) => {
    if (!Object.keys(updates).length) { toast.error('Set at least one field to update'); return; }
    try {
      const { data } = await axios.post(`${API}/service/jobs/bulk-update`, { ids: [...selected], updates });
      toast.success(`${data.modified} job(s) updated`);
      setBulkEditOpen(false);
      fetchJobs();
    } catch {
      toast.error('Bulk update failed');
    }
  };
  const handleExport = async () => {
    try {
      const ids = selected.size ? [...selected] : sorted.map((j) => j.id);
      const res = await axios.post(`${API}/service/jobs/export`, { job_ids: ids, format: 'excel' }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Service_Jobs_${selectedFY}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Excel downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({ doc_type: [], technician: [], supervisor: [], service_head: [], job_type: [] });
    // iter-120: Default date range is Indian FY YTD
    setActivePreset('ytd');
    const [a, b] = computePresetRange('ytd');
    setDateFrom(a); setDateTo(b);
    setStatusGroup('');
    // iter-120: Also purge any saved session snapshot so Clear is an unambiguous reset
    try { sessionStorage.removeItem(FILTERS_STORAGE_KEY); } catch { /* noop */ }
  };
  const activeFilterCount =
    Object.values(filters).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0)
    + (search ? 1 : 0) + (dateFrom || dateTo ? 1 : 0) + (statusGroup ? 1 : 0);

  // Header actions
  const headerActions = (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <button
        onClick={() => navigate('/service/jobs/new')}
        data-testid="new-job-btn"
        className="px-3 sm:px-4 py-2 rounded-xl text-white text-[12px] sm:text-[13px] font-medium shadow-sm shadow-indigo-500/20 hover:shadow-md transition-all flex items-center gap-1.5 sm:gap-2"
        style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
      >
        <Plus size={15} />
        <span className="hidden sm:inline">New Job Card</span>
        <span className="sm:hidden">New</span>
      </button>
      <button
        onClick={() => navigate('/service/jobs/new?quick=1')}
        data-testid="new-quick-job-btn"
        className="px-3 sm:px-4 py-2 rounded-xl text-white text-[12px] sm:text-[13px] font-medium shadow-sm shadow-emerald-500/20 hover:shadow-md transition-all flex items-center gap-1.5 sm:gap-2"
        style={{ background: 'linear-gradient(135deg, #059669 0%, #0EA5E9 100%)' }}
        title="Create a Quick Service Job (auto-numbered QS1, QS2, …)"
      >
        <Plus size={15} />
        <span className="hidden sm:inline">Quick Job</span>
        <span className="sm:hidden">QS</span>
      </button>
      <button
        onClick={handleExport}
        data-testid="export-excel-btn"
        className="px-3 sm:px-3.5 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white text-[12px] sm:text-[13px] font-medium flex items-center gap-1.5 sm:gap-2"
      >
        <FileSpreadsheet size={15} />
        <span className="hidden sm:inline">Export Excel</span>
      </button>
      <button
        onClick={fetchJobs}
        data-testid="refresh-btn"
        className="px-3 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white text-[13px] font-medium flex items-center gap-2"
      >
        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  );

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="All Service Jobs" subtitle={`${filtered.length} of ${jobs.length} jobs · FY ${selectedFY}`} actions={headerActions}>
      <div className="space-y-4" data-testid="service-jobs-page">
        {/* Toolbar */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-4">
          {/* Date Range presets — hidden entirely when admin disables job_cards.filters.date_range */}
          {jcFilters.date_range && (
            <DateRangePresets
              activePreset={activePreset} setActivePreset={setActivePreset}
              dateFrom={dateFrom} setDateFrom={setDateFrom}
              dateTo={dateTo} setDateTo={setDateTo}
              sortDir={sortDir} setSortDir={setSortDir}
            />
          )}

          <div className={`flex flex-wrap items-center gap-3 ${jcFilters.date_range ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
            <div className="flex items-center gap-2">
              <label className="text-[12px] font-medium text-slate-500">FY</label>
              <select
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                data-testid="fy-select"
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              >
                {fyOptions().map((fy) => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>

            {(jcFilters.reg_no || jcFilters.party_name || jcFilters.mobile) && (
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Job No / Reg / Chassis / Customer / Mobile / Model"
                  data-testid="search-input"
                  className="w-full pl-9 pr-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            )}

            {[
              { k: 'doc_type', label: 'Status', mk: 'status', vk: 'status' },
              { k: 'service_head', label: 'Service Head', mk: 'service_head', vk: 'service_head' },
              { k: 'job_type', label: 'Job Type', mk: 'job_type', vk: 'job_type' },
              { k: 'supervisor', label: 'Supervisor', mk: 'supervisor', vk: 'supervisor' },
              { k: 'technician', label: 'Technician', mk: 'technician', vk: 'technician' },
            ].filter(({ vk }) => jcFilters[vk]).map(({ k, label, mk }) => (
              <MultiSelectFilter
                key={k}
                label={label}
                options={masters[mk] || []}
                selected={filters[k]}
                onChange={(arr) => setFilters((f) => ({ ...f, [k]: arr }))}
                testId={`filter-${k}`}
              />
            ))}

            {/* Show PDI / Show Delivered toggles — off by default */}
            {jcFilters.show_pdi && (
              <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none px-2 py-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={showPdi}
                  onChange={(e) => setShowPdi(e.target.checked)}
                  data-testid="show-pdi-toggle"
                  className="w-4 h-4 rounded border-slate-300 text-sky-600"
                />
                Show PDI
              </label>
            )}
            {jcFilters.show_delivered && (
              <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none px-2 py-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={showDelivered}
                  onChange={(e) => setShowDelivered(e.target.checked)}
                  data-testid="show-delivered-toggle"
                  className="w-4 h-4 rounded border-slate-300 text-sky-600"
                />
                Show Delivered
              </label>
            )}

            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                className="text-[12px] text-indigo-600 hover:text-indigo-800 px-2 py-1 inline-flex items-center gap-1"
              >
                <X size={13} /> Clear ({activeFilterCount})
              </button>
            )}

            {/* SLA quick filter chips */}
            {jcFilters.sla_filter && (
              <div className="inline-flex items-center gap-1 ml-2">
                {[
                  { id: 'all', label: 'All', tone: 'slate' },
                  { id: 'overdue', label: 'Overdue', tone: 'rose', Icon: AlertCircle },
                  { id: 'at_risk', label: 'At Risk', tone: 'amber', Icon: Clock },
                  { id: 'active', label: 'Active', tone: 'sky' },
                ].map((s) => {
                  const active = slaFilter === s.id;
                  const toneCls = active
                    ? s.tone === 'rose' ? 'bg-rose-600 text-white border-rose-600'
                      : s.tone === 'amber' ? 'bg-amber-500 text-white border-amber-500'
                      : s.tone === 'sky' ? 'bg-sky-600 text-white border-sky-600'
                      : 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300';
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSlaFilter(s.id)}
                      data-testid={`sla-filter-${s.id}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-medium border transition-all ${toneCls}`}
                    >
                      {s.Icon && <s.Icon size={12} />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* iter-114 — Type segmented chip: All / Regular / Quick */}
            <div className="inline-flex items-center gap-1 ml-2 rounded-full border border-slate-200 bg-white p-0.5" data-testid="job-type-segment">
              {[
                { id: 'all', label: 'All' },
                { id: 'regular', label: 'Regular' },
                { id: 'quick', label: 'Quick' },
              ].map((s) => {
                const active = jobTypeView === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setJobTypeView(s.id)}
                    data-testid={`type-filter-${s.id}`}
                    className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                      active
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-emerald-700'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>

            {/* Page size + nav arrows on the RIGHT */}
            <div className="ml-auto">
              <PageSizeNav
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                testIdPrefix="jobs-pagesize"
              />
            </div>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 border border-indigo-100">
              <Filter size={15} className="text-indigo-600" />
              <span className="text-[13px] text-indigo-900 font-medium">{selected.size} selected</span>
              <span className="flex-1" />
              <button onClick={() => setBulkEditOpen(true)} data-testid="bulk-edit-btn" className="px-3 py-1.5 rounded-lg bg-white text-indigo-700 border border-indigo-200 text-[12.5px] font-medium hover:bg-indigo-50 flex items-center gap-1.5">
                <Pencil size={13} /> Bulk Edit
              </button>
              {canDeleteService && (
                <button onClick={handleBulkDelete} data-testid="bulk-delete-btn" className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-[12.5px] font-medium hover:bg-rose-700 flex items-center gap-1.5">
                  <Trash2 size={13} /> Bulk Delete
                </button>
              )}
              <button onClick={() => setSelected(new Set())} className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-white text-[12.5px]">
                <X size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Top horizontal scrollbar — mirrors the table's bottom scroll */}
        <div className="rounded-xl bg-white/70 backdrop-blur border border-white/50 px-2 py-1">
          <div className="overflow-x-auto" ref={topScrollRef} onScroll={onTopScroll} data-testid="top-scroll">
            <div style={{ width: tableWidth || '100%', height: 1 }} />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
          <div className="overflow-auto max-h-[70vh] relative" ref={tableScrollRef} onScroll={onTableScroll}>
            <table className="w-full text-[13px] min-w-[1400px]">
              <thead className="sticky top-0 z-20">
                <tr className="text-left bg-sky-600 text-white border-b border-sky-700">
                  <th className="p-3 w-10 bg-sky-600">
                    <input
                      type="checkbox"
                      checked={paged.length > 0 && paged.every((j) => selected.has(j.id))}
                      onChange={toggleAllOnPage}
                      data-testid="select-all-checkbox"
                      className="w-4 h-4 rounded border-white/40 text-sky-700 focus:ring-sky-300 bg-white/90"
                    />
                  </th>
                  <th className="p-3 w-8 bg-sky-600" />
                  {[
                    ['job_no_int', 'Job No', 'w-[80px]', true],
                    ['doc_type', 'Status', 'w-[140px]', jcCols.doc_type],
                    ['__job_hours', 'Job Hours', 'w-[100px]', true],
                    ['__days_delay', 'Days Delay', 'w-[110px]', true],
                    ['party_name', 'Party Name', 'min-w-[200px]', jcCols.party_name],
                    ['reg_no', 'Reg No', 'w-[120px]', jcCols.reg_no],
                    ['model', 'Model', 'min-w-[160px]', jcCols.model],
                    ['service_head', 'Service Head', 'w-[140px]', jcCols.service_head],
                    ['job_type', 'Job Type', 'w-[160px]', jcCols.job_type],
                    ['supervisor', 'Supervisor', 'w-[120px]', jcCols.supervisor],
                    ['technician', 'Technician', 'w-[120px]', jcCols.technician],
                    ['vehicle_in_date', 'Veh. In', 'w-[110px]', jcCols.vehicle_in_date],
                    ['vehicle_out_date', 'Veh. Out', 'w-[110px]', jcCols.vehicle_out_date],
                  ].map(([k, label, w, visible]) => (
                    <th key={k} className={`p-3 font-semibold text-white select-none whitespace-nowrap bg-sky-600 ${w} ${visible ? '' : 'hidden'}`}>
                      {k.startsWith('__') ? (
                        <span className="inline-flex items-center gap-1">
                          {label}
                        </span>
                      ) : (
                        <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-sky-100">
                          {label}
                          <ArrowUpDown size={12} className={`opacity-70 ${sortKey === k ? 'opacity-100 text-yellow-200' : ''}`} />
                        </button>
                      )}
                    </th>
                  ))}
                  <th className="p-3 text-right font-semibold text-white sticky right-0 bg-sky-600 w-[120px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={16} className="p-12 text-center"><Loader2 size={22} className="animate-spin text-indigo-500 mx-auto" /></td></tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="p-12 text-center">
                      <div className="text-slate-400 text-[13px]">
                        No jobs found. {jobs.length === 0 && (
                          <button onClick={() => navigate('/service/upload')} className="text-indigo-600 hover:underline font-medium">Upload data</button>
                        )} or <button onClick={() => navigate('/service/jobs/new')} className="text-indigo-600 hover:underline font-medium">create a new job</button>.
                      </div>
                    </td>
                  </tr>
                ) : paged.map((j, recIdx) => {
                  const isSel = selected.has(j.id);
                  const isExp = expanded.has(j.id);
                  // Alternate row coloring per record: 1st row = white, 2nd row = pale blue (sky-50)
                  const baseTone = recIdx % 2 === 0 ? 'bg-white' : 'bg-sky-50/60';
                  const stickyTone = recIdx % 2 === 0 ? 'bg-white' : 'bg-sky-50';
                  return (
                    <React.Fragment key={j.id}>
                      {/* Primary row */}
                      <tr className={`border-b border-slate-100 transition-colors ${isSel ? 'bg-indigo-50/60' : isExp ? 'bg-indigo-50/30' : `${baseTone} hover:bg-sky-100/40`}`}>
                        <td className="p-3 align-top">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => togglePick(j.id)}
                            data-testid={`row-checkbox-${j.id}`}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="p-3 align-top">
                          <button
                            onClick={() => toggleExpand(j.id)}
                            data-testid={`expand-${j.id}`}
                            className={`p-1 rounded-md text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 transition-all ${isExp ? 'rotate-90 text-indigo-700 bg-indigo-50' : ''}`}
                            title={isExp ? 'Collapse' : 'Expand'}
                          >
                            <ChevronRight size={14} />
                          </button>
                        </td>
                        <td className="p-3 align-top">
                          <div className="inline-flex items-center gap-1.5">
                            <SLADot status={j.sla_status} />
                            <button
                              onClick={() => toggleExpand(j.id)}
                              className={`font-bold text-[14.5px] hover:underline ${j.is_quick ? 'text-emerald-700' : 'text-green-900'}`}
                              data-testid={`job-no-${j.id}`}
                              title={j.is_quick ? 'Quick Service Job' : 'Service Job'}
                            >
                              {j.job_no}
                            </button>
                          </div>
                        </td>
                        <td className={`p-3 align-top ${jcCols.doc_type ? '' : 'hidden'}`}><StatusPill value={j.doc_type} /></td>
                        <td className="p-3 align-top text-slate-700 whitespace-nowrap font-mono text-[12px]" data-testid={`job-hours-${j.id}`}>
                          {(() => {
                            const v = jobMetrics(j).jobHours;
                            if (v) return v;
                            return (
                              <span className="text-slate-300" title="Add Ramp In + Ramp Out times on Edit Job to compute Job Hours">—</span>
                            );
                          })()}
                        </td>
                        <td className="p-3 align-top whitespace-nowrap font-mono text-[12px]" data-testid={`days-delay-${j.id}`}>
                          {(() => {
                            const d = jobMetrics(j).daysDelay;
                            if (!d) return (
                              <span className="text-slate-300" title="Add Vehicle In + Ramp In times on Edit Job to compute Days Delay">—</span>
                            );
                            const negative = d.startsWith('-');
                            return <span className={negative ? 'text-rose-600' : 'text-slate-700'}>{d}</span>;
                          })()}
                        </td>
                        <td className={`p-3 align-top ${jcCols.party_name ? '' : 'hidden'}`} data-testid={`party-name-${j.id}`}>
                          <div className="text-blue-900 font-bold text-[14.5px]">{j.party_name || '—'}</div>
                          {j.last_notify_at && (
                            <div className="mt-1">
                              <LastNotifyChip job={j} />
                            </div>
                          )}
                        </td>
                        <td className={`p-3 align-top text-slate-600 font-mono ${jcCols.reg_no ? '' : 'hidden'}`}>{j.reg_no || '—'}</td>
                        <td className={`p-3 align-top text-slate-600 ${jcCols.model ? '' : 'hidden'}`}>{j.model || '—'}</td>
                        <td className={`p-3 align-top text-slate-600 ${jcCols.service_head ? '' : 'hidden'}`}>{j.service_head || '—'}</td>
                        <td className={`p-3 align-top text-slate-600 ${jcCols.job_type ? '' : 'hidden'}`}>{j.job_type || '—'}</td>
                        <td className={`p-3 align-top text-slate-600 ${jcCols.supervisor ? '' : 'hidden'}`}>{j.supervisor || '—'}</td>
                        <td className={`p-3 align-top text-slate-600 ${jcCols.technician ? '' : 'hidden'}`}>{j.technician || '—'}</td>
                        <td className={`p-3 align-top text-slate-500 whitespace-nowrap ${jcCols.vehicle_in_date ? '' : 'hidden'}`}>{j.vehicle_in_date || '—'}</td>
                        <td className={`p-3 align-top text-slate-500 whitespace-nowrap ${jcCols.vehicle_out_date ? '' : 'hidden'}`}>{j.vehicle_out_date || '—'}</td>
                        <td className="p-3 align-top text-right whitespace-nowrap sticky right-0" style={{}}>
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-l-md ${stickyTone}`}>
                            <ActionBtn icon={isExp ? ChevronDown : ChevronRight} title={isExp ? 'Collapse' : 'Expand'} onClick={() => toggleExpand(j.id)} testId={`view-${j.id}`} />
                            <ActionBtn icon={Pencil} title="Edit" onClick={() => navigate(`/service/jobs/${j.id}/edit?from=jobs`)} testId={`edit-${j.id}`} />
                            {canDeleteService && (
                              <ActionBtn icon={Trash2} title="Delete" tone="danger" onClick={() => handleDelete(j)} testId={`delete-${j.id}`} />
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Secondary row — extra fields including Customer's Voice (wrapped to 2 lines) */}
                      <tr className={`border-b border-slate-100 ${isSel ? 'bg-indigo-50/60' : isExp ? 'bg-indigo-50/30' : baseTone}`}>
                        <td colSpan={2} />
                        <td colSpan={2} />
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.chassis_no ? (
                            <><span className="text-slate-400">Chassis:</span> <span className="font-mono text-slate-600">{j.chassis_no || '—'}</span></>
                          ) : null}
                        </td>
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.mobile_no_1 ? (
                            <><span className="text-slate-400">Mobile:</span> <span className="text-slate-700">{j.mobile_no_1 || '—'}</span></>
                          ) : null}
                        </td>
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.booking_no ? (
                            <><span className="text-slate-400">Booking:</span> <span className="text-slate-700">{j.booking_no || '—'}</span></>
                          ) : null}
                        </td>
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.customer_type ? (
                            <><span className="text-slate-400">Cust Type:</span> <span className="text-slate-700">{j.customer_type || '—'}</span></>
                          ) : null}
                        </td>
                        <td colSpan={5} className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.customer_voice ? (
                            j.customer_voice ? (
                              <span className="text-slate-700 line-clamp-2 leading-snug" title={j.customer_voice}>
                                <span className="text-slate-400">Voice:</span> {j.customer_voice}
                              </span>
                            ) : (
                              <span className="text-slate-400">Voice: —</span>
                            )
                          ) : null}
                        </td>
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.kms ? (
                            <><span className="text-slate-400">KMS:</span> <span className="text-slate-700">{j.kms || '—'}</span></>
                          ) : null}
                        </td>
                        <td className="px-3 pb-3 pt-0 align-top text-[11.5px]">
                          {jcCols.color ? (
                            <><span className="text-slate-400">Color:</span> <span className="text-slate-700">{j.color || '—'}</span></>
                          ) : null}
                        </td>
                        <td className={`sticky right-0 ${stickyTone}`} />
                      </tr>

                      {/* Expanded inline detail panel */}
                      {isExp && <ExpandedRow job={j} />}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      <BulkEditModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        count={selected.size}
        onApply={handleBulkUpdate}
        fields={[
          { key: 'doc_type', label: 'Status', options: masters.status },
          { key: 'service_head', label: 'Service Head', options: masters.service_head },
          { key: 'job_type', label: 'Job Type', options: masters.job_type },
          { key: 'supervisor', label: 'Supervisor', options: masters.supervisor },
          { key: 'technician', label: 'Technician', options: masters.technician },
        ]}
      />
    </ServiceLayout>
  );
}

// Expanded detail row — horizontal 3-panel layout (Job / Customer / Vehicle)
// IMPORTANT: the parent <td colSpan={16}> spans the FULL table width, which on
// mobile/iPad is far wider than the viewport (the table is inside an
// overflow-x-auto wrapper). To keep the panel content always within the visible
// viewport we anchor the inner wrapper with `position: sticky; left: 0` and pin
// its width to `100vw`. This way the panel re-flows to fit the device width
// regardless of how far the table is horizontally scrolled.
function ExpandedRow({ job }) {
  const Section = ({ title, items }) => (
    <div className="rounded-xl bg-white/70 border border-slate-200/70 p-4">
      <h4 className="text-[12px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</h4>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-[12.5px]">
        {items.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-slate-500 whitespace-nowrap">{k}</dt>
            <dd className="text-slate-800 break-words min-w-0">{v || <span className="text-slate-400">—</span>}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );

  return (
    <tr className="bg-gradient-to-r from-sky-50/60 via-indigo-50/40 to-fuchsia-50/30 border-b border-slate-200">
      <td colSpan={16} className="p-0">
        <div
          className="sticky left-0 p-3 sm:p-4"
          style={{ width: '100vw', maxWidth: '100vw', boxSizing: 'border-box' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`expanded-${job.id}`}>
            <Section title="Job Details" items={[
              ['Job No', job.job_no],
              ['Source Job No', job.source_job_no],
              ['SLA', <SLABadgeInline status={job.sla_status} daysLate={job.days_late} />],
              ['Status', job.doc_type],
              ['Job Date', job.job_date],
              ['Service Head', job.service_head],
              ['Job Category', job.job_category],
              ['Job Type', job.job_type],
              ['Supervisor', job.supervisor],
              ['Technician', job.technician],
              ['Customer Type', job.customer_type],
              ["Customer's Voice", job.customer_voice],
              ['Vehicle In', `${job.vehicle_in_date || '—'} ${job.vehicle_in_time || ''}`.trim()],
              ['Expected Delivery', job.expected_delivery_date],
              ['Ramp In', `${job.ramp_in_date || '—'} ${job.ramp_in_time || ''}`.trim()],
              ['Ramp Out', `${job.ramp_out_date || '—'} ${job.ramp_out_time || ''}`.trim()],
            ]} />
            <Section title="Customer Details" items={[
              ['Party Name', job.party_name],
              ['Mobile 1', job.mobile_no_1],
              ['Mobile 2', job.mobile_no_2],
              ['Booking No', job.booking_no],
              ['Email', job.email],
            ]} />
            <Section title="Vehicle Details" items={[
              ['Brand', job.brand_name],
              ['Model', job.model],
              ['Variant', job.variant],
              ['Color', job.color],
              ['Chassis No', job.chassis_no],
              ['Engine No', job.engine_no],
              ['Battery No', job.battery_no],
              ['Charger No', job.charger_no],
              ['Controller No', job.controller_no],
              ['Reg No', job.reg_no],
              ['KMS', job.kms],
            ]} />
          </div>
        </div>
      </td>
    </tr>
  );
}

// Expanded detail row — horizontal 3-panel layout (Job / Customer / Vehicle)

const ActionBtn = ({ icon: Icon, title, onClick, tone, testId }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded-lg border transition-colors ${tone === 'danger'
      ? 'border-rose-200 text-rose-600 hover:bg-rose-50'
      : 'border-slate-200 text-slate-600 hover:text-indigo-700 hover:border-indigo-200 hover:bg-indigo-50'}`}
  >
    <Icon size={14} />
  </button>
);
